import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { BannerStyle } from '@mylighthouse/prism-ember/types/components/prism/banner';
import type { MultiFactorError, UserCredential } from 'firebase/auth';
import {
  AUTHENTICATION,
  EMAIL_REGEX,
  GIP_NAMES,
  GIP_NAMES_WITHOUT_PASSWORD,
  type GIPName,
  type GIPNameWithoutPassword,
  PROVIDER_TYPES,
  type ProviderType,
  RESPONSE_CODE,
  SSO_TYPES,
  type SSOType,
} from 'frontend-login/utils/constant-values';
import {
  isMultiFactorError,
  type LoginResponse,
} from 'password-form/services/mfa-service';
import nextWithDefault from 'password-form/utils/auth-utils';
import { FIREBASE_ERROR_CODES } from 'password-form/utils/constant-values';

// This type is the same as in PrismBannerSignature link
interface BannerLink {
  action?: (event: MouseEvent) => unknown;
  icon?: boolean;
  text: string;
  url?: string;
}

interface LoginOption {
  identity_provider: {
    gip_name: GIPName;
    location?: string;
    name: string;
    provider_type: string;
  };
  is_migrated: boolean;
  location: string;
  type: string;
}

interface LoginOptionResponse {
  login_options: LoginOption[];
}

interface StorageData {
  connectWithNewPlatformInProcess: boolean;
  gipName: GIPName;
}

type ParamsType = {
  next?: string;
};

const ResponseCode = {
  OK: 'ok',
  MfaSent: 'mfa_sent',
  ProviderConfirmationRequired: 'provider_confirmation_required',
  Error: 'error',
} as const;

type ResponseCode = keyof typeof ResponseCode;

interface ResponseMeta {
  error_message?: string;

  login_option?: LoginOption;
}

export default class SharedAuthService extends Service {
  @service declare authentication: Services['authentication'];
  @service declare customFetch: Services['custom-fetch'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare mfaService: Services['mfa-service'];
  @service declare router: Services['router'];

  @tracked bannerStyle: BannerStyle = 'critical';
  @tracked connectWithNewPlatformConfirmation = false;
  @tracked emailInputHasErrors = false;
  @tracked emailInputValue = '';
  @tracked errorLink: BannerLink | undefined = undefined;
  @tracked errorMessage = '';
  @tracked errorTitle = '';
  /**
   * This variable is used before redirecting to distinguish which redirecting message should be shown
   */
  @tracked isBeingRedirected = false;
  @tracked isRedirecting = false;
  @tracked loadingButtonType: null | GIPName = null;
  @tracked loginOptions: LoginOption[] | null = null;
  @tracked password = '';
  // Support legacy 2FA screen
  @tracked show2FAScreen = false;
  @tracked showError = false;
  @tracked showSSOConfirmationScreen = false;

  activeGipName: GIPName | undefined;
  activeLoginOption: LoginOption | undefined;
  authenticatedEmail: string | undefined | null = null;
  /**
   * Cached response from firebase after login
   */
  cachedCredential: UserCredential | null = null;
  /**
   * Cached response from login-and-migrate endpoint
   */
  cachedMigrateResult: LoginResponse | null = null;
  connectWithNewPlatformInProcess = false;
  needsMfa = false;
  oldGipName: GIPName | undefined;
  signInWithSocialActions = {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    [GIP_NAMES.GOOGLE]: this.signInWithGoogle,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    [GIP_NAMES.MICROSOFT]: this.signInWithMicrosoft,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    [GIP_NAMES.SAML]: this.signInWithSaml,
  };
  token: string | undefined;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultSsoLoginOptions() {
    return [
      {
        type: SSO_TYPES.GOOGLE,
        gipType: GIP_NAMES.GOOGLE,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickAction: () => this.signInWithGoogle(GIP_NAMES.GOOGLE),
      },
      {
        type: SSO_TYPES.MICROSOFT,
        gipType: GIP_NAMES.MICROSOFT,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickAction: () => this.signInWithMicrosoft(GIP_NAMES.MICROSOFT),
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOnlyPasswordLoginOption() {
    return this.loginOptions?.length === 1 && this.passwordLoginOption;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEmailValid() {
    return EMAIL_REGEX.test(this.emailInputValue);
  }

  get passwordLoginOption(): LoginOption | undefined {
    if (!this.loginOptions?.length) {
      return undefined;
    }

    return this.loginOptions.find(
      (option) =>
        option.identity_provider.provider_type ===
        PROVIDER_TYPES.EMAIL_AND_PASSWORD,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get samlLoginOption() {
    return (
      this.loginOptions?.find(
        (option) =>
          option.identity_provider.provider_type === PROVIDER_TYPES.SAML,
      ) ?? this.activeLoginOption
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldVerifyNewPlatform() {
    return Boolean(
      this.connectWithNewPlatformInProcess &&
      this.activeLoginOption &&
      this.activeGipName,
    );
  }

  /**
   * Filter the login options to only show the social login options
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ssoLoginOptions() {
    // Add default social login options if there are none
    if (!this.loginOptions?.length && !this.hasOnlyPasswordLoginOption) {
      return this.defaultSsoLoginOptions;
    }

    if (!this.loginOptions) {
      return [];
    }

    const filteredLoginOptions = this.loginOptions.filter(
      (option) =>
        option.identity_provider.provider_type !==
        PROVIDER_TYPES.EMAIL_AND_PASSWORD,
    );
    return filteredLoginOptions.map((option: LoginOption) => {
      const { provider_type, gip_name, name } = option.identity_provider;
      const isSaml = provider_type === PROVIDER_TYPES.SAML;

      /**
       * If the provider is SAML, its gip_name is saml.<saml_provider_name>
       * The ButtonSingleSignOn component expects google, microsoft or saml for its type
       * Therefore, for SAML we use provider name as gip_name
       */
      const gipType: GIPName = isSaml ? GIP_NAMES.SAML : gip_name;
      const type = isSaml
        ? PROVIDER_TYPES.SAML
        : (name.toLowerCase() as SSOType);

      return {
        type,
        gipType,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickAction: () =>
          this.signInWithSocialActions[
            gipType as keyof typeof this.signInWithSocialActions
          ](gipType),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get userHasloginOptions() {
    return Boolean(this.loginOptions?.length);
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.router.on('routeDidChange', () => {
      // @ts-expect-error: Property 'queryParams' does not exist on type 'RouteInfoWithAttributes | RouteInfo | null'.
      const { queryParams } = this.router.currentRoute;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (queryParams['isRedirecting'] !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.isRedirecting = queryParams['isRedirecting'] === 'true';
      }
    });
  }

  /**
   * Continue the login/registration flow after the user has logged in with their IDP (after redirect) or password
   */

  async completeLoginProcess(
    params = {},
    afterRedirect = false,
  ): Promise<void> {
    let result = this.cachedMigrateResult;
    let userEmail: string | null = null;

    if (!this.cachedMigrateResult) {
      // If the user didn't need to migrate, we need to get their credentials from cachedCredential (from firebase)
      const userCredentialFallback =
        this.cachedCredential ?? this.mfaService.userCredential;
      if (!userCredentialFallback || !userCredentialFallback.user.email) {
        this.showErrorMessage();
        return;
      }

      userEmail = userCredentialFallback.user.email;
      this.trackSentryUser(userEmail);

      try {
        this.needsMfa = await this.authentication.shouldShowMfa(
          userEmail,
          userCredentialFallback.providerId ?? null,
        );
      } catch (error) {
        // Check for specific identity provider not found error
        if (
          error instanceof Error &&
          error.message === 'IDENTITY_PROVIDER_NOT_FOUND'
        ) {
          this.errorTracking.captureException(error);
          this.showErrorMessage({
            message: this.intl.t('mfa.error.identity_provider_not_found'),
            title: this.intl.t('general.error_title'),
          });
          return;
        }

        this.errorTracking.captureException(error);
        this.showErrorMessage();
        return;
      }

      result = await this.verifyUserToken(params);
    } else {
      this.needsMfa = true;
    }

    if (!result) {
      this.showErrorMessage();
      return;
    }

    if (result.token) {
      this.token = result.token;
    }

    // Resolve email from all possible sources
    userEmail = result.email || userEmail || this.emailInputValue;
    this.emailInputValue = userEmail;
    this.trackSentryUser(userEmail);
    if (!result.response.ok) {
      this.handleErrors(result, true);
      return;
    }

    if (userEmail) {
      await this.fetchLoginOptions(userEmail, false);
    }

    /**
     * Get the data that was stored in localStorage before being redirected to the authentication method
     * The data is used to continue the flow after the user has selected a new platform
     */
    const storageData = localStorage.getItem(AUTHENTICATION);
    if (storageData) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const data = JSON.parse(storageData);
      localStorage.removeItem(AUTHENTICATION);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.connectWithNewPlatformInProcess =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        data.connectWithNewPlatformInProcess;
    }

    if (this.connectWithNewPlatformInProcess) {
      this.setIsRedirecting(false);
      this.connectWithNewPlatformInProcess = false;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.confirmNewPlatform(result);
      return;
    }

    await this.processAuthenticationResponse(result, afterRedirect);
  }

  async fetchLoginOptions(email: string, redirect = true): Promise<void> {
    try {
      const response = await this.customFetch.fetch<LoginOptionResponse>(
        '/sso/login-options/',
        {
          email,
        },
      );
      this.loginOptions = response.login_options;

      // If the user has only one login option, redirect to it
      if (
        redirect &&
        this.loginOptions?.length === 1 &&
        !this.hasOnlyPasswordLoginOption
      ) {
        const identityProvider = this.loginOptions[0]?.identity_provider;
        const providerType =
          identityProvider?.provider_type === PROVIDER_TYPES.SAML
            ? identityProvider.provider_type
            : identityProvider?.gip_name;
        if (providerType) {
          this.redirectToIdp(providerType);
        }
      }
    } catch (error) {
      this.showErrorMessage();
      this.errorTracking.captureException(error);
    }
  }

  /**
   * This function handles the errors received from the server and shows the appropriate error message.
   */

  handleErrors(result: LoginResponse, isPasswordSignIn = false): void {
    const { status } = result.response;
    const { code } = result.data;

    if (code === RESPONSE_CODE.INVALID_CREDENTIALS && isPasswordSignIn) {
      this.showErrorMessage({
        message: this.isStaffEmail()
          ? this.intl.t('login.error.staff_social_login')
          : this.intl.t('login.error.invalid_credentials'),
        title: '',
      });
      return;
    }

    if (code === RESPONSE_CODE.INVALID_CREDENTIALS && !isPasswordSignIn) {
      this.showErrorMessage({
        message: this.intl.t('login.error.no_access'),
        title: '',
      });
      return;
    }

    if (code === RESPONSE_CODE.EMAIL_ALREADY_EXISTS) {
      if (this.isStaffEmail()) {
        this.showErrorMessage({
          message: this.intl.t('login.error.staff_social_login'),
          title: '',
        });
        return;
      }
      this.showErrorMessage({
        link: {
          text: this.intl.t('login.sign_in'),
          url: '/login',
        },
        message: this.intl.t('login.error.email_already_exists'),
        title: '',
      });
      return;
    }

    if (code === RESPONSE_CODE.MFA_ENFORCED) {
      this.authenticatedEmail = this.emailInputValue;
      this.trackSentryUser(this.emailInputValue);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.authentication.redirectToMfa(nextWithDefault(result.data.next));
      return;
    }

    if (
      code === RESPONSE_CODE.LOGIN_OPTION_UNAVAILABLE ||
      code === RESPONSE_CODE.ACCOUNT_ISSUE ||
      code === RESPONSE_CODE.LOGIN_OPTION_NOT_FOUND ||
      code === RESPONSE_CODE.GENERIC
    ) {
      this.showErrorMessage();
      this.errorTracking.captureException(result.data);
      return;
    }

    if (status === 500) {
      this.showErrorMessage();
      this.errorTracking.captureException(result.response);
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async handleNonOkResponse(
    code: ResponseCode,
    meta?: ResponseMeta,
    afterRedirect = false,
  ): Promise<void> {
    switch (code) {
      case RESPONSE_CODE.MFA_SENT:
        this.show2FAScreen = true;
        break;

      case RESPONSE_CODE.PROVIDER_CONFIRMATION_REQUIRED:
        if (meta?.login_option) {
          this.setIsRedirecting(false);
          this.activeLoginOption = meta.login_option;
          this.activeGipName = meta.login_option.identity_provider.gip_name;

          /**
           * If the user wants to connect with a new platform, we need to show the connect with new platform screen,
           * otherwise show the SSO confirmation screen
           */
          if (afterRedirect) {
            if (this.loginOptions) {
              this.oldGipName =
                this.loginOptions[0]?.identity_provider.gip_name;
            }
            this.connectWithNewPlatformConfirmation = true;
          } else {
            this.showSSOConfirmationScreen = true;
          }
        }
        break;

      default:
        this.showErrorMessage({
          title: meta?.error_message,
        });
        this.errorTracking.captureException(
          new Error(`Unexpected response code: ${code}`),
        );
    }
  }

  /**
   * Handle the logic after the user has been redirected back to the application after logging in with their IDP
   */

  async handleRedirectLogic(
    result: UserCredential | null,
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    error: unknown | null = null,
  ): Promise<void> {
    if (!error && !result) {
      if (!this.isBeingRedirected) {
        this.setIsRedirecting(false);
      }
      return;
    }

    if (error) {
      if (
        isMultiFactorError(error) &&
        error.code === FIREBASE_ERROR_CODES.MULTI_FACTOR_REQUIRED
      ) {
        this.authenticatedEmail = error.customData._serverResponse.email;
        this.trackSentryUser(error.customData._serverResponse.email);
        await this.fetchLoginOptions(this.authenticatedEmail, false);

        // First we need to check if the user needs to confirm a new platform
        const storageData = localStorage.getItem(AUTHENTICATION);
        let shouldShowConfirmation = false;

        if (storageData) {
          localStorage.removeItem(AUTHENTICATION);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const data = JSON.parse(storageData);
          const hasGipNameInOptions = this.loginOptions?.some(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (option) => option.identity_provider.gip_name === data.gipName,
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (data.gipName && !hasGipNameInOptions) {
            shouldShowConfirmation = true;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            this.activeGipName = data.gipName;
            // eslint-disable-next-line max-depth
            if (this.loginOptions?.length) {
              this.oldGipName =
                this.loginOptions[0]?.identity_provider.gip_name;
            }

            this.connectWithNewPlatformInProcess = true;
          }
        }

        if (shouldShowConfirmation) {
          this.connectWithNewPlatformConfirmation = true;
          this.setIsRedirecting(false);

          return;
        }

        // If we don't need to show the confirmation screen, proceed with MFA
        await this.solveMfa(error);
        this.setIsRedirecting(false);

        return;
      }
      this.setIsRedirecting(false);
      this.showErrorMessage();
      this.errorTracking.captureException(error);
      return;
    }

    if (!result || !result.user.email) {
      this.setIsRedirecting(false);
      this.showErrorMessage();
      return;
    }

    this.setIsRedirecting(true);
    // @ts-expect-error: Property 'queryParams' does not exist on type 'RouteInfoWithAttributes | RouteInfo | null'.
    const { queryParams } = this.router.currentRoute;
    this.cachedCredential = result;

    await this.completeLoginProcess(
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        next: nextWithDefault(queryParams['next']),
      },
      true,
    );
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async handleSuccessfulAuth(next?: string): Promise<void> {
    if (!next) {
      return;
    }

    try {
      if (this.needsMfa) {
        this.authenticatedEmail = this.emailInputValue;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.authentication.redirectToMfa(nextWithDefault(next));
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.redirectToApp(next);
    } catch (error) {
      this.showErrorMessage();
      this.errorTracking.captureException(error);
    }
  }

  isSignInMethodWithoutPassword(
    idp: GIPName | ProviderType,
  ): idp is GIPNameWithoutPassword {
    return Object.values(GIP_NAMES_WITHOUT_PASSWORD).includes(
      idp as GIPNameWithoutPassword,
    );
  }

  private isStaffEmail(): boolean {
    return this.emailInputValue.toLowerCase().endsWith('@mylighthouse.com');
  }

  /**
   * This function processes the authentication response from the server and handles different response codes accordingly.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async processAuthenticationResponse(
    response: LoginResponse,
    afterRedirect = false,
  ) {
    const { code, next, meta } = response.data;

    if (code !== RESPONSE_CODE.OK) {
      return this.handleNonOkResponse(
        code as ResponseCode,
        meta,
        afterRedirect,
      );
    }

    return this.handleSuccessfulAuth(next);
  }

  async redirectToApp(next?: string): Promise<void> {
    // If we don't have a token yet (e.g., coming from MFA with continueLogin=true),
    if (!this.token) {
      const result = await this.verifyUserToken();
      if (result.token) {
        this.token = result.token;
      }
    }

    if (
      this.connectWithNewPlatformInProcess &&
      this.token &&
      this.activeGipName
    ) {
      await this.authentication.acceptSso(this.token, this.activeGipName);
    }
    window.location.href = nextWithDefault(next);
  }

  /**
   * Redirect to the selected identity provider
   * The user is redirected to their IDP if it's set in the URL or if it is their only login option
   */

  redirectToIdp(idp: GIPName | ProviderType): void {
    this.setIsRedirecting(true);
    this.isBeingRedirected = true;

    if (this.isSignInMethodWithoutPassword(idp)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.signInWithSocialActions[idp]();
    } else if (idp.startsWith('saml.')) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.signInWithSaml(GIP_NAMES.SAML, idp);
    } else {
      this.setIsRedirecting(false);
      this.showErrorMessage();
    }
  }

  setIsRedirecting(value: boolean): void {
    this.isRedirecting = value;
    if (this.isRedirecting) {
      this.router.transitionTo({ queryParams: { isRedirecting: value } });
    } else {
      this.router.transitionTo({ queryParams: { isRedirecting: undefined } });
    }
  }

  /**
   * This function is used when the user has MFA enabled or enforced.
   *
   * Sets the MFA resolver to validate the status of the user, to check if the user has MFA enabled (already existing method)
   * or if the user is enforced and needs to enroll a MFA method.
   */

  async solveMfa(error: MultiFactorError): Promise<void> {
    // @ts-expect-error: Property 'queryParams' does not exist on type 'RouteInfoWithAttributes | RouteInfo | null'.
    const { queryParams } = this.router.currentRoute;

    this.mfaService.setMultiFactorResolver(error);
    const params: ParamsType = {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (queryParams['next']) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      params.next = nextWithDefault(queryParams['next']);
    }
    await this.router.transitionTo('mfa', { queryParams: params });
  }

  storeLocalStorage(data: StorageData): void {
    localStorage.setItem(AUTHENTICATION, JSON.stringify(data));
  }

  /**
   * Attach the user's email to Sentry so any subsequent error captured in this
   * session can be traced back to the affected user. Safe to call repeatedly;
   * empty values are ignored so we never overwrite a known email with nothing.
   */
  trackSentryUser(email: string | null | undefined): void {
    if (email) {
      this.errorTracking.setUser({ email });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async verifyUserToken(params = {}) {
    const userCredential =
      this.cachedCredential ?? this.mfaService.userCredential;

    if (!userCredential?.user) {
      throw new Error('No user credentials available');
    }

    const idToken = await userCredential.user.getIdToken();
    const result = await this.authentication.verifyTokenOnUM(idToken, params);
    result.token = idToken;

    return result;
  }

  /**
   * This function is used to continue the flow after the user has selected a new platform to connect with.
   * This can happen when the user wants to change their login method, either by their own will or by their administrator forcing them to use SSO.

    * Extra logging is added to track the user's actions and the response from the server. As some account switches result in errors.
  */

  @action
  async confirmNewPlatform(
    loginResponse: LoginResponse | null = null,
  ): Promise<void> {
    this.showSSOConfirmationScreen = false;
    try {
      if (!this.token || !this.activeLoginOption || !this.activeGipName) {
        this.errorTracking.addBreadcrumb({
          message: `confirmPlatform parameters missing: token: ${this.token} | oldGipName: ${this.oldGipName} | newGipName: ${this.activeGipName}`,
          data: this.activeLoginOption,
        });
        throw new Error('Token or gip is undefined');
      }

      const result = await this.authentication.acceptSso(
        this.token,
        this.activeGipName,
      );

      this.errorTracking.addBreadcrumb({
        message: `User is trying to connect with a new platform: oldGipName: ${this.oldGipName}, newGipName: ${this.activeGipName}`,
        data: {
          result,
          activeLoginOption: this.activeLoginOption,
        },
      });

      if (result.ok && loginResponse) {
        this.connectWithNewPlatformInProcess = false;
        await this.processAuthenticationResponse(loginResponse);
        return;
      }

      // Redirect to the new platform
      if (result.ok && this.activeLoginOption) {
        const idp =
          this.activeLoginOption.identity_provider.provider_type ===
          PROVIDER_TYPES.SAML
            ? PROVIDER_TYPES.SAML
            : this.activeLoginOption.identity_provider.gip_name;
        this.redirectToIdp(idp);
        this.connectWithNewPlatformInProcess = false;
        return;
      }
      this.connectWithNewPlatformInProcess = false;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const data: LoginResponse = await result.json();
      this.handleErrors(data);
    } catch (error) {
      this.errorTracking.addBreadcrumb({
        message: `User failed to connect with a new platform: oldGipName: ${this.oldGipName}, newGipName: ${this.activeGipName}`,
        data: {
          error,
          activeLoginOption: this.activeLoginOption,
        },
      });
      this.showErrorMessage();
      this.errorTracking.captureException(error);
    }
  }

  @action
  reset(): void {
    this.emailInputValue = '';
    this.password = '';
    this.loginOptions = null;
    this.emailInputHasErrors = false;
    this.showError = false;
    this.errorTitle = '';
    this.errorMessage = '';
    this.show2FAScreen = false;
  }

  @action
  setEmail(event: Event): void {
    // Reset the login options if the user changes the email
    if (event.isTrusted && this.loginOptions) {
      this.loginOptions = null;
    }
    this.emailInputHasErrors = false;
    this.showError = false;
    this.password = '';
    this.emailInputValue = (event.target as HTMLInputElement).value;
  }

  @action
  setPassword(event: Event): void {
    this.password = (event.target as HTMLInputElement).value;
  }

  @action showErrorMessage(options?: {
    bannerStyle?: BannerStyle;
    link?: BannerLink;
    message?: string;
    title?: string;
  }): void {
    this.errorTitle = options?.title ?? this.intl.t('general.error_title');

    this.errorMessage = options?.message ?? this.intl.t('login.error.general');

    this.bannerStyle = options?.bannerStyle ?? 'critical';

    this.errorLink = options?.link;

    this.showError = true;
    this.setIsRedirecting(false);
  }

  @action
  async signInWithGoogle(gipName: GIPName | null = null): Promise<void> {
    try {
      if (gipName) {
        this.loadingButtonType = gipName;
      }
      this.setIsRedirecting(true);

      // Store the data in localStorage before redirecting to the IDP page
      const storageData: StorageData = {
        connectWithNewPlatformInProcess: this.connectWithNewPlatformInProcess,
        gipName: GIP_NAMES.GOOGLE,
      };

      localStorage.setItem(AUTHENTICATION, JSON.stringify(storageData));

      await this.authentication.signInWithGoogle();
    } catch (error) {
      this.errorTracking.captureException(error);
      this.showErrorMessage();
    }
  }

  @action
  async signInWithMicrosoft(gipName: GIPName | null = null): Promise<void> {
    try {
      if (gipName) {
        this.loadingButtonType = gipName;
      }
      this.setIsRedirecting(true);

      // Store the data in localStorage before redirecting to the IDP page
      const storageData: StorageData = {
        connectWithNewPlatformInProcess: this.connectWithNewPlatformInProcess,
        gipName: GIP_NAMES.MICROSOFT,
      };

      localStorage.setItem(AUTHENTICATION, JSON.stringify(storageData));

      await this.authentication.signInWithMicrosoft();
    } catch (error) {
      this.errorTracking.captureException(error);
      this.showErrorMessage();
    }
  }

  @action
  async signInWithSaml(
    providerType: GIPName | null = null,
    gipName: string | null = null,
  ): Promise<void> {
    try {
      if (providerType) {
        this.loadingButtonType = providerType;
      }
      this.setIsRedirecting(true);
      const location = this.samlLoginOption?.identity_provider.location;
      if (location && location.startsWith('/')) {
        window.location.href = location;
        return;
      }

      // Store the data in localStorage before redirecting to the IDP page
      // Use a default GIP name (SAML) if none is provided
      const storageData: StorageData = {
        connectWithNewPlatformInProcess: this.connectWithNewPlatformInProcess,
        gipName: GIP_NAMES.SAML,
      };

      // If we have a specific SAML provider, use that instead
      if (this.samlLoginOption?.identity_provider.gip_name) {
        storageData.gipName = this.samlLoginOption.identity_provider.gip_name;
      }

      localStorage.setItem(AUTHENTICATION, JSON.stringify(storageData));

      if (!this.samlLoginOption && !gipName) {
        this.showErrorMessage();
        return;
      }

      const gip = gipName || this.samlLoginOption?.identity_provider.gip_name;
      if (!gip) {
        throw new Error('GIP name is undefined');
      }
      await this.authentication.signInWithSaml(gip);
    } catch (error) {
      this.errorTracking.captureException(error);
      this.showErrorMessage();
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'shared-auth': SharedAuthService;
  }
}
